import React, { useState, useEffect } from 'react';
import { Plus, DollarSign, Calendar, Trash2, Edit2, Save, X, AlertCircle, Settings, UserCog } from 'lucide-react';
import api from '../config/api';
import { categoryColor } from '../utils/categoryColors';
import { useLanguage } from '../context/LanguageContext';
import { showNotification } from '../components/GlobalNotification';
import AddExpenseModal from '../components/AddExpenseModal';
import ExpenseItemsModal from '../components/ExpenseItemsModal';

function Expenses() {
  const { t } = useLanguage();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Modal states
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [showItemsModal, setShowItemsModal] = useState(false);
  
  // Expense items management state
  const [expenseItems, setExpenseItems] = useState([]);
  
  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    fetchExpenses();
    fetchExpenseItems();
  }, []);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/expenses');
      setExpenses(response.data);
    } catch (error) {
      setError(t('Failed to fetch expenses'));
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenseItems = async () => {
    try {
      const response = await api.get('/api/expense-items');
      setExpenseItems(response.data);
    } catch (error) {
      console.error('Failed to fetch expense items');
    }
  };

  const handleAddExpense = async (formData) => {
    try {
      // Round to 2 decimals rather than truncating - editing an expense keeps
      // decimals, so creating one has to as well
      const amount = Math.round((parseFloat(formData.amount) || 0) * 100) / 100;

      const response = await api.post('/api/expenses', {
        category: formData.category.trim(),
        description: formData.description.trim(),
        amount: amount,
        date: formData.date
      });

      setExpenses([response.data, ...expenses]);
      setShowAddExpenseModal(false);
      showNotification('success', t('Expense added successfully!'));
    } catch (error) {
      showNotification('error', t('Failed to add expense'));
    }
  };

  const addExpenseItem = async (itemName) => {
    try {
      const response = await api.post('/api/expense-items', { name: itemName });
      setExpenseItems([...expenseItems, response.data]);
      showNotification('success', t('Expense item added successfully!'));
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to add expense item';
      showNotification('error', message);
    }
  };

  const editExpenseItem = async (itemId, newName) => {
    try {
      const response = await api.put(`/api/expense-items/${itemId}`, { name: newName });
      setExpenseItems(expenseItems.map(item => 
        item._id === itemId 
          ? response.data
          : item
      ));
      showNotification('success', t('Expense item updated successfully!'));
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update expense item';
      showNotification('error', message);
    }
  };

  const deleteExpenseItem = async (itemId) => {
    try {
      await api.delete(`/api/expense-items/${itemId}`);
      setExpenseItems(expenseItems.filter(item => item._id !== itemId));
      showNotification('success', t('Expense item deleted successfully!'));
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete expense item';
      showNotification('error', message);
    }
  };

  const handleEdit = (expense) => {
    setEditingId(expense._id);
    setEditData({
      category: expense.category || '',
      description: expense.description,
      amount: expense.amount,
      date: expense.date.split('T')[0]
    });
  };

  const handleSaveEdit = async (id) => {
    try {
      const response = await api.put(`/api/expenses/${id}`, {
        ...editData,
        amount: parseFloat(editData.amount) || 0
      });
      setExpenses(expenses.map(expense => 
        expense._id === id ? response.data : expense
      ));
      setEditingId(null);
      setEditData({});
      showNotification('success', t('Expense updated successfully!'));
    } catch (error) {
      showNotification('error', t('Failed to update expense'));
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('Are you sure you want to delete this expense?'))) {
      return;
    }

    try {
      await api.delete(`/api/expenses/${id}`);
      setExpenses(expenses.filter(expense => expense._id !== id));
      showNotification('success', t('Expense deleted successfully!'));
    } catch (error) {
      showNotification('error', t('Failed to delete expense'));
    }
  };

  // Calculate totals
  const totalAllExpenses = expenses.reduce((sum, expense) => sum + expense.amount, 0);
  const todayExpenses = expenses.filter(expense => {
    const today = new Date().toDateString();
    const expenseDate = new Date(expense.date).toDateString();
    return today === expenseDate;
  });
  const totalTodayExpenses = todayExpenses.reduce((sum, expense) => sum + expense.amount, 0);

  return (
    <div className="space-y-4 sm:space-y-8">
      {/* Header with Action Buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold text-gray-900">{t('Expenses')}</h2>
          <p className="text-sm sm:text-base text-gray-600">{t('Track and manage your business expenses')}</p>
        </div>
        {/* Equal halves on a phone, natural width once there is room */}
        <div className="flex gap-2 sm:gap-3 flex-shrink-0">
          <button
            onClick={() => setShowItemsModal(true)}
            className="flex-1 sm:flex-none px-4 py-2 min-h-[44px] whitespace-nowrap bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center"
          >
            <Settings className="h-4 w-4 mr-2 flex-shrink-0" />
            {t('Manage Items')}
          </button>
          <button
            onClick={() => setShowAddExpenseModal(true)}
            className="flex-1 sm:flex-none px-4 sm:px-6 py-2 min-h-[44px] whitespace-nowrap bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <Plus className="h-5 w-5 mr-2 flex-shrink-0" />
            {t('Add Expense')}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Calendar className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">{t('Today\'s Expenses')}</h3>
              <p className="text-2xl font-bold text-gray-900">Rs. {totalTodayExpenses.toFixed(2)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">{t('Total Expenses')}</h3>
              <p className="text-2xl font-bold text-gray-900">Rs. {totalAllExpenses.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Expenses List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Expenses by Category')}</h3>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('Loading expenses...')}</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <p className="text-red-600">{error}</p>
          </div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-8">
            <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">{t('No Expenses Yet')}</h4>
            <p className="text-gray-500 mb-4">{t('Start by adding your first expense.')}</p>
            <button
              onClick={() => setShowAddExpenseModal(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('Add First Expense')}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {(() => {
              // Group expenses by category
              const groupedExpenses = expenses.reduce((groups, expense) => {
                const category = expense.category || 'Uncategorized';
                if (!groups[category]) {
                  groups[category] = [];
                }
                groups[category].push(expense);
                return groups;
              }, {});

              // Calculate total for each category
              const categoriesWithTotals = Object.entries(groupedExpenses).map(([category, categoryExpenses]) => ({
                category,
                expenses: categoryExpenses,
                total: categoryExpenses.reduce((sum, expense) => sum + expense.amount, 0)
              }));

              // Sort categories by total amount (highest first)
              categoriesWithTotals.sort((a, b) => b.total - a.total);

              return categoriesWithTotals.map(({ category, expenses: categoryExpenses, total }) => {
                const color = categoryColor(category);
                return (
                <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Category Header - tinted in the category's colour so the
                      groups can be told apart at a glance, not just read */}
                  <div className={`${color.soft} px-4 py-3 border-b border-gray-200`}>
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-medium text-gray-900 capitalize flex items-center gap-2">
                        <span className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${color.dot}`} aria-hidden="true" />
                        {category}
                      </h4>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-500">
                          {categoryExpenses.length} {categoryExpenses.length === 1 ? 'expense' : 'expenses'}
                        </span>
                        <span className={`text-lg font-semibold ${color.text}`}>
                          Rs. {total.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Category Expenses */}
                  <div className="divide-y divide-gray-200">
                    {categoryExpenses.map((expense) => (
                      <div key={expense._id} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex-1">
                          {editingId === expense._id ? (
                            <div className="space-y-3">
                              {/* Category Dropdown */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Category')}</label>
                                  <select
                                    value={editData.category || ''}
                                    onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  >
                                    <option value="">{t('Select category')}</option>
                                    {expenseItems.map((item) => (
                                      <option key={item._id} value={item.name}>
                                        {item.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Amount (Rs.)')}</label>
                                  <input
                                    type="number"
                                    value={editData.amount}
                                    onChange={(e) => setEditData({ ...editData, amount: e.target.value })}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    min="0"
                                    step="0.01"
                                    placeholder="0"
                                  />
                                </div>
                              </div>
                              
                              {/* Description Field */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">{t('Description')}</label>
                                <input
                                  type="text"
                                  value={editData.description}
                                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  placeholder={t('Enter expense description')}
                                />
                              </div>
                              
                              {/* Date Field */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('Date')}</label>
                                  <input
                                    type="date"
                                    value={editData.date}
                                    onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-sm text-gray-600">
                                  <span className="font-medium text-gray-900 flex items-center gap-1.5">
                                    <span
                                      className={`h-2 w-2 rounded-full flex-shrink-0 ${categoryColor(expense.category).dot}`}
                                      aria-hidden="true"
                                    />
                                    {expense.category || 'No Category'}
                                  </span>
                                  <span className="hidden sm:inline">•</span>
                                  <span>{new Date(expense.date).toLocaleDateString()}</span>
                                  <span className="hidden sm:inline">•</span>
                                  <span className="text-base font-semibold text-gray-900">Rs. {expense.amount.toFixed(2)}</span>
                                </div>
                              </div>
                              {expense.description && (
                                <div className="text-sm text-gray-600 mt-1">
                                  {expense.description}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-start space-x-2 ml-2 sm:ml-4 mt-2 sm:mt-0">
                          {editingId === expense._id ? (
                            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                              <button
                                onClick={() => handleSaveEdit(expense._id)}
                                className="flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                              >
                                <Save className="h-4 w-4 mr-1" />
                                <span className="sm:hidden">{t('Save')}</span>
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                              >
                                <X className="h-4 w-4 mr-1" />
                                <span className="sm:hidden">{t('Cancel')}</span>
                              </button>
                            </div>
                          ) : expense.source === 'staff' ? (
                            /* Mirrored from the staff ledger. Editing it here
                               would leave the two disagreeing, so it is changed
                               from the Staff page instead. */
                            <span
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded-lg whitespace-nowrap"
                              title={t('Managed from the Staff page')}
                            >
                              <UserCog className="h-3.5 w-3.5" />
                              <span className="hidden sm:inline">{t('Staff')}</span>
                            </span>
                          ) : (
                            <div className="flex space-x-1 sm:space-x-2">
                              <button
                                onClick={() => handleEdit(expense)}
                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                title={t('Edit expense')}
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(expense._id)}
                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                title={t('Delete expense')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                );
              });
            })()}
          </div>
        )}
      </div>

      {/* Modals */}
      <AddExpenseModal
        isOpen={showAddExpenseModal}
        onClose={() => setShowAddExpenseModal(false)}
        onSubmit={handleAddExpense}
        expenseItems={expenseItems}
      />

      <ExpenseItemsModal
        isOpen={showItemsModal}
        onClose={() => setShowItemsModal(false)}
        expenseItems={expenseItems}
        onAddItem={addExpenseItem}
        onEditItem={editExpenseItem}
        onDeleteItem={deleteExpenseItem}
      />
    </div>
  );
}

export default Expenses;